<script lang="ts" setup>
import { computed, onActivated, ref } from "vue";
import UserAvatar from "@/components/UserAvatar.vue";
import { useRouter } from "vue-router";
import ReactionCounter from "@/components/mobile/reply/ReactionCounter.vue";
import ReactionMate from "@/components/mobile/reply/ReactionMate.vue";
import escapeHtml from "@/modules/escape-html";

import { timeAgo } from "@/modules/common";
import { Moment } from "@/types/entity";
import { removeHtmlTag } from "@/tools/text";

// Props & Emit
interface ThreadItemProps {
  moment: Moment;
  enableTextCollapsed?: boolean;
  enableShowMoreLink?: boolean; // 串文：hasNext为true时，显示查看更多按钮
  showInteractionArea?: boolean;
  showReplyList?: boolean;
  isTreehole?: boolean;
}

const props: ThreadItemProps = withDefaults(defineProps<ThreadItemProps>(), {
  showComment: true,
  enableReactionBtn: true,
  enableTextCollapsed: true,
  enableAudioPlay: false,
  enableShowMoreLink: false,
  showAvatar: true,
  showInteractionArea: true,
  showReplyList: false,
  isTreehole: false,
});

const emit = defineEmits<{
  (e: "threadClick", uuid: string): void;
  (e: "commentClick"): void;
  (e: "reactionClick", moment: Moment): void;
}>();
const router = useRouter();
// const user = useGlobalUser();
const escapedContent = computed(() => {
  if (!props.moment.text) {
    return "";
  }
  return escapeHtml(props.moment.text);
});
const displayContent = computed(() => {
  if (!props.moment.text) {
    return "";
  }
  // 1. 先按照换行符分割，保留7行
  const splitByLine = escapedContent.value.split("\n").slice(0, 7);
  // 2. 再按照字符数分割，保留前100个字符
  return splitByLine.join("\n").slice(0, 128);
});

const needCollapsed =
  props.enableTextCollapsed! &&
  !!props.moment.text &&
  props.moment.text!.length !== displayContent.value.length;

const textCollapsed = ref<boolean>(needCollapsed);
const routeToMateHome = (e: UIEvent, mateUuid: string) => {
  e.stopPropagation();
  toMateDetail(mateUuid);
};

const clickMomentAvatar = (e: UIEvent, mateUuid: string) => {
  router.push({
    name: "treeholeSelfSetting",
    query: { mateUuid },
  });
};

const toMateDetail = async (uuid: string) => {
  router.push({
    name: "mateHome",
    params: { uuid: uuid },
    query: { isTreehole: 1 },
  });
};

const onThreadClick = () => {
  router.push({
    name: "treeholeDetail",
    params: { uuid: props.moment.uuid },
  });
};
onActivated(() => {
  textCollapsed.value = needCollapsed;
});
</script>
<template>
  <div
    class="flex flex-col w-full border-b pt-2 pb-2 cursor-pointer"
    @click="onThreadClick"
  >
    <div class="flex gap-1">
      <!-- Avatar -->
      <div class="flex flex-col items-center">
        <user-avatar
          :mate="moment.mate"
          class="h-4 shrink-0 xl:h-11 cursor-pointer max-w-[40px]"
          @click.stop="(e) => clickMomentAvatar(e, moment.mate.uuid)"
        />
      </div>
      <!--  标题 & 类型 & 时间  -->
      <div class="flex-grow flex flex-col overflow-hidden justify-center">
        <div
          class="flex flex-col flex-grow gap-1"
          @click="emit('threadClick', moment.uuid!)"
        >
          <div class="flex-grow flex flex-col justify-center">
            <div class="flex justify-between gap-1">
              <div
                class="text-base-content/50 text-xs line-clamp-1"
                @click.stop="(e) => routeToMateHome(e, moment.mate.uuid)"
              >
                {{ moment.mate.name ?? "" }}
              </div>

              <div class="gap-2 flex text-xs">
                <div
                  v-if="moment.dtCreate"
                  class="text-base-content/50 whitespace-nowrap"
                >
                  {{ timeAgo(moment.dtCreate) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-1">
      <div class="flex flex-col relative">
        <div class="flex flex-col gap-2">
          <!-- 文本内容 -->
          <div
            class="text-base-content bg-base-200 whitespace-pre-line px-4 py-3 rounded-lg min-h-[40px]"
          >
            <html-ellipsis
              v-if="props.enableTextCollapsed"
              :collapse-text="$t('Buttons.collapse')"
              :content="moment.text"
              :expand-text="$t('Buttons.viewAll')"
              :rows="5"
            />
            <div
              v-else
              class="prose prose-sm break-words leading-relaxed text-base-content"
              v-html="moment.text"
            ></div>
          </div>
        </div>

        <!-- 交互 -->
        <div v-if="showInteractionArea" class="flex flex-col gap-2 mt-2">
          <reaction-mate :reply="moment.reply" />
          <div class="flex w-full justify-between items-end">
            <div class="flex gap-2 flex-wrap">
              <button
                class="btn btn-xs font-normal rounded text-base-content/60"
                @click="emit('commentClick')"
              >
                <i class="iconify mingcute--comment-line" />
                <span v-if="moment.reply && moment.reply.replyCount > 0">
                  {{ moment.reply?.replyCount }}
                </span>
              </button>

              <audio-play
                v-if="moment.audioMedia"
                :mate-tone-id="moment.mate.mateToneId!"
                :msg="moment.text"
                :url="moment.audioMedia.url"
              />
              <reaction-counter :reply="moment.reply" />
            </div>
            <div class="flex gap-1 items-center">
              <div
                class="btn btn-xs btn-ghost btn-square"
                @click.stop="emit('reactionClick', moment)"
              >
                <i class="iconify mingcute--more-1-line" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- <div
      class="mt-2 bg-base-200 p-2 rounded-md flex flex-col gap-1"
      v-if="showReplyList && moment.reply?.descendantzz?.length"
    >
      <div v-for="reply in moment.reply?.descendantzz" :key="reply.id">
        <div class="flex items-center gap-2">
          <div class="line-clamp-3">
            <span
              class="text-primary/70"
              @click.stop="toMateDetail(reply.mate.uuid)"
            >
              {{ reply.mate.name }}
            </span>
            <span class="text-base-content/80 text-xs">
              :
              <template v-if="reply.parentId !== reply.rootId && reply.parent">
                <span>
                  回复
                  <b>@{{ reply.parent.mate.name }}</b>
                </span>
              </template>
              {{ removeHtmlTag(reply.content) }}
            </span>
          </div>
        </div>
      </div>
      <div
        v-if="moment.reply?.replyCount > 3"
        class="text-base-content/50 text-xs"
      >
        <div class="text-base-content/50 text-xs">
          {{ $t("Buttons.viewAll") }}
        </div>
      </div>
    </div> -->
  </div>
</template>
<style lang="postcss" scoped>
:deep(.tooltip::before) {
  font-size: 8px;
  text-align: left;
  white-space: normal;
  word-wrap: break-word;
  width: 100%;
  padding: 0 5px;
}
</style>
